import { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { Icon, Button } from '@bosch/react-frok'
import RichChip from '@/ui/components/RichChip/RichChip'
import { useTranslation } from 'react-i18next'
import storage from '@/utils/storage'
import preferencesApi from '@/utils/api/preferencesApi'
import { useConfig } from '@/hooks/useConfig'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useSpace } from '@/hooks/useSpace'
import { preferredLinesKey, defaultLineKey } from '@/utils/preferenceKeys'
import { useModal } from '@/contexts/modalContext'
import LineSelectionForm from '@/ui/components/LineSelectionForm/LineSelectionForm'
import HorizontalPager from '@/ui/components/HorizontalPager/HorizontalPager'
import useScrollPaging from '@/hooks/useScrollPaging'
import useWindowSize from '@/hooks/useWindowSize'
import type { LineOption } from '@/utils/storage'
import type { QuickLineSelectionProps } from './types/QuickLineSelectionTypes'

import './QuickLineSelection.scss'

const QuickLineSelection = ({ 
    currentFilters, 
    availableLines, 
    onToggleLine,
    isDisabled = false,
    isHidden = false,
}: QuickLineSelectionProps) => {
    const { showModal, hideModal } = useModal()
    const { getConfig } = useConfig()
    const { getApiToken } = useAuthentication()
    // the preferred lines are the ones of the space
    const { getActiveSpace } = useSpace()
    const spaceId = getActiveSpace()?.id ?? ''
    const { t } = useTranslation('general')
    const [isSaving, setIsSaving] = useState(false)
    const [defaultLinesRefresh, setPreferredLinesRefresh] = useState(0)
    const sessionLinesRef = useRef<LineOption[]>([])
    const chipsRef = useRef<HTMLDivElement>(null)
    const { isMobile } = useWindowSize()
    const [sessionLinesVersion, setSessionLinesVersion] = useState(0)

    // Listen for default lines changes to trigger re-render and deselect removed lines
    useEffect(() => {
        const handleDefaultLinesChanged = () => {
            setPreferredLinesRefresh(prev => prev + 1)
        }

        window.addEventListener('mma:defaultLinesChanged', handleDefaultLinesChanged)
        return () => window.removeEventListener('mma:defaultLinesChanged', handleDefaultLinesChanged)
    }, [])

    const preferredLines = useMemo(() => {
        const defaultLines = storage.getPreferredLines(spaceId)
        
        if (!defaultLines || defaultLines.length === 0) {
            return []
        }

        return defaultLines
        // defaultLinesRefresh is not read, it makes the memo read the storage again after the lines changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultLinesRefresh, spaceId])

    // Read through a ref: the effect below must not run again (and toggle again) when the parent passes a new callback
    const onToggleLineRef = useRef(onToggleLine)
    useEffect(() => {
        onToggleLineRef.current = onToggleLine
    }, [onToggleLine])

    useEffect(() => {
        // The lines of the assistant are not known yet (metadata still loading after a switch): nothing can be judged
        if (availableLines.length === 0) return

        const selectedLines = currentFilters?.line || []
        const preferredValues = new Set(preferredLines.map(line => line.value))
        const knownValues = new Set([...preferredValues, ...sessionLinesRef.current.map(line => line.value)])

        const additions = selectedLines.filter((line: LineOption) =>
            !knownValues.has(line.value) && availableLines.some(available => available.value === line.value)
        )

        if (additions.length > 0) {
            sessionLinesRef.current = [...sessionLinesRef.current, ...additions]
            for (const line of additions) knownValues.add(line.value)
            setSessionLinesVersion(prev => prev + 1)
        }

        for (const line of selectedLines) {
            if (!knownValues.has(line.value)) {
                onToggleLineRef.current(line)
            }
        }
    }, [currentFilters, preferredLines, availableLines])

    const displayedLines = useMemo(() => {
        // Filter preferred lines to only show those available in current assistant
        const availablePreferred = preferredLines.filter(line =>
            availableLines.some(available => available.value === line.value)
        )
        const extra = sessionLinesRef.current.filter(line =>
            !preferredLines.some(p => p.value === line.value) && availableLines.some(available => available.value === line.value)
        )
        return [...availablePreferred, ...extra]
        // sessionLinesVersion is not read, it makes the memo read sessionLinesRef again
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferredLines, availableLines, sessionLinesVersion])

    // The dialog lists the lines that are shown as pills too, so a line that came with a handover is in it (after the
    // preferred ones). It is a preferred line only when the user saves the dialog.
    const dialogLines = useMemo(() => {
        const extra = sessionLinesRef.current.filter(line => !preferredLines.some(p => p.value === line.value))
        return [...preferredLines, ...extra]
        // sessionLinesVersion is not read, it makes the memo read sessionLinesRef again
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferredLines, sessionLinesVersion])

    const handleChipClick = useCallback((line: { value: string; label: string }) => {
        if (!isDisabled)
            onToggleLine(line)
    }, [onToggleLine, isDisabled])

    const currentFiltersRef = useRef(currentFilters)
    const preferredLinesRef = useRef(preferredLines)
    const displayedLinesRef = useRef(displayedLines)

    useEffect(() => {
        currentFiltersRef.current = currentFilters
    }, [currentFilters])

    useEffect(() => {
        preferredLinesRef.current = preferredLines
    }, [preferredLines])

    const availableLinesRef = useRef(availableLines)
    useEffect(() => {
        availableLinesRef.current = availableLines
    }, [availableLines])

    useEffect(() => {
        displayedLinesRef.current = displayedLines
    }, [displayedLines])

    const handleSaveLines = useCallback(async (selectedLines: LineOption[]) => {
        setIsSaving(true)
        try {
            const newLineValues = new Set(selectedLines.map(line => line.value))
            const displayedAtOpen = displayedLinesRef.current || []
            
            const removedLines = displayedAtOpen.filter(line => !newLineValues.has(line.value))
            
            const currentlySelected = currentFiltersRef.current?.line || []
            for (const removedLine of removedLines) {
                // Check if this removed line is currently selected
                const isCurrentlySelected = currentlySelected.some(sel => sel.value === removedLine.value)
                if (isCurrentlySelected) {
                    const lineToDeselect = {
                        value: removedLine.value,
                        label: removedLine.label || removedLine.value
                    }
                    onToggleLine(lineToDeselect)
                }
            }
            
            await new Promise(resolve => setTimeout(resolve, 50))
            
            // The dialog lists the lines of this assistant only: the preferred lines it does not have stay
            const keptLines = preferredLinesRef.current.filter(line =>
                !availableLinesRef.current.some(available => available.value === line.value)
            )
            const savedLines = [...selectedLines, ...keptLines]

            // The default line is always the first preferred line (None when there are none)
            const defaultLine = savedLines[0]?.value ?? null

            storage.setPreferredLines(savedLines, spaceId)
            storage.setDefaultLine(defaultLine, spaceId)
            
            const config = getConfig()
            const endpoint = config?.endpoint
            if (endpoint && spaceId && config?.features?.preferencesApi) {
                try {
                    const token = await getApiToken()
                    const lineValues = savedLines.map(line => line.value)
                    await preferencesApi.patchPreferences(
                        endpoint,
                        token,
                        { [preferredLinesKey(spaceId)]: lineValues, [defaultLineKey(spaceId)]: defaultLine }
                    )
                } catch (error) {
                    console.error('Failed to sync preferred lines to backend:', error)
                }
            }
            
            window.dispatchEvent(new Event('mma:defaultLinesChanged'))
            hideModal()
        } finally {
            setIsSaving(false)
        }
    }, [spaceId, onToggleLine, getConfig, getApiToken, hideModal])

    const handleSettingsClick = useCallback(() => {
        if (isDisabled) return

        showModal({
            title: t('general:settings:productionLines'),
            content: (
                <LineSelectionForm
                    availableLines={availableLines}
                    initialLines={dialogLines.filter(line => availableLines.some(available => available.value === line.value))}
                    onSave={handleSaveLines}
                    onCancel={hideModal}
                    isLoading={isSaving}
                />
            ),
            onConfirmClick: () => {}, // Not used, handled by form buttons
            onCancelClick: hideModal,
            showConfirmButton: false,
            showCancelButton: false,
        })
    }, [showModal, hideModal, availableLines, dialogLines, isSaving, isDisabled, handleSaveLines, t])

    const hasChips = displayedLines.length > 0
    const { page, pageCount, goToPage } = useScrollPaging(chipsRef, displayedLines.length)
    // Desktop: buttons and page indicator when the chips do not fit. Mobile: swipe, the chips snap
    const showPager = !isMobile && pageCount > 1
    const selectedLineValues = currentFilters?.line?.map((l) => l.value) || []

    return (
        <div
            className={`quick-line-selection ${isDisabled ? 'quick-line-selection--disabled' : ''} ${isHidden ? 'quick-line-selection--hidden' : ''}`}
            aria-hidden={isHidden || undefined}
        >
            <div className={`quick-line-selection__container ${hasChips ? '' : 'quick-line-selection__container--empty'}`}>
                {hasChips && <div className="quick-line-selection__chips-area">
                <div ref={chipsRef} className="quick-line-selection__chips">
                    {displayedLines.map((line) => {
                        const isSelected = selectedLineValues.includes(line.value)
                        return (
                            <div 
                                key={line.value} 
                                className={`quick-line-selection__chip-wrapper ${
                                    isSelected ? 'quick-line-selection__chip-wrapper--selected' : ''
                                }`}
                            >
                                <RichChip
                                     label={
                                        <div className="quick-line-selection__chip__label">
                                            <Icon iconName="assembly-line" />
                                            <span>{line.label}</span>
                                        </div>
                                    }
                                    className="quick-line-selection__chip"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleChipClick(line)
                                    }}
                                />
                            </div>
                        )
                    })}
                </div>
                {showPager && <HorizontalPager page={page} pageCount={pageCount} onPageChange={goToPage} />}
                </div>}
                <Button
                    mode="integrated"
                    icon="settings-editor"
                    aria-label="Manage production lines"
                    onClick={handleSettingsClick}
                    disabled={isDisabled}
                    className="quick-line-selection__settings-btn"
                />
            </div>
        </div>
    )
}

export default QuickLineSelection
